import React from "react";
import { PlusIcon, XIcon } from "../../../components/icons";
import { v4 as uuidv4 } from "uuid";
import FormSection from "../../../components/forms/FormSection";
import EditorCard, { AddRowButton } from "../../../components/forms/EditorCard";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Input } from "../../../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Tooltip } from "../../../components/ui/tooltip";
import { PromptDataType, PromptModel } from "../types/prompt-model";
import {
  AssemblyReferenceConditionWrite,
  AssemblyReferenceCategoryConditionWrite,
  RangeComparisonOperator,
} from "../types/assembly-condition";

/**
 * A condition being edited in the UI. `_key` is a client-only stable identity
 * for the React list — it is NOT sent to the server (lineage is set only by the
 * clone pipeline), so it must be stripped before the condition is written.
 */
export interface EditableCondition extends AssemblyReferenceConditionWrite {
  _key: string;
}

/**
 * A blank condition with a fresh client-only key, defaulting the target
 * prompt to the reference's own prompt.
 */
export const createEmptyCondition = (ownPromptObjectIdentifier: string): EditableCondition => ({
  isYes: null,
  fuzzyMatch: false,
  minimumDecimalAnswer: null,
  maximumDecimalAnswer: null,
  textAnswer: null,
  allCategoriesMatch: false,
  decimalAnswer: null,
  decimalOperator: null,
  dateOperator: null,
  startDate: null,
  endDate: null,
  dateAnswer: null,
  _key: uuidv4(),
  targetPromptObjectIdentifier: ownPromptObjectIdentifier,
  categoryConditions: [],
});

const OPERATOR_LABELS: Record<RangeComparisonOperator, string> = {
  [RangeComparisonOperator.Equal]: "Equal to",
  [RangeComparisonOperator.GreaterThan]: "Greater than",
  [RangeComparisonOperator.LessThan]: "Less than",
  [RangeComparisonOperator.GreaterThanOrEqual]: "Greater than or equal",
  [RangeComparisonOperator.LessThanOrEqual]: "Less than or equal",
  [RangeComparisonOperator.Range]: "Between (range)",
};

const OPERATORS: RangeComparisonOperator[] = [
  RangeComparisonOperator.Equal,
  RangeComparisonOperator.GreaterThan,
  RangeComparisonOperator.LessThan,
  RangeComparisonOperator.GreaterThanOrEqual,
  RangeComparisonOperator.LessThanOrEqual,
  RangeComparisonOperator.Range,
];

/** The hint shown in place of fields the user cannot fill in yet. */
const Hint = ({ children }: { children: React.ReactNode }) => (
  <div className="text-caption text-muted-foreground">{children}</div>
);

/** A checkbox whose label reads as an option rather than as body text. */
const OptionCheckbox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) => {
  const id = React.useId();
  return (
    <div className="flex flex-row items-center gap-2 py-1.5">
      <Checkbox id={id} checked={checked} onCheckedChange={(next) => onChange(next === true)} />
      <label htmlFor={id} className="text-body2 text-muted-foreground">
        {label}
      </label>
    </div>
  );
};

/** A comparison operator; "" is none chosen yet, which shows an empty field. */
const OperatorSelect = ({
  value,
  onChange,
}: {
  value: RangeComparisonOperator | "";
  onChange: (value: RangeComparisonOperator | null) => void;
}) => (
  <Select
    value={value}
    onValueChange={(next) => onChange((next || null) as RangeComparisonOperator | null)}
  >
    <SelectTrigger>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {OPERATORS.map((op) => (
        <SelectItem key={op} value={op}>
          {OPERATOR_LABELS[op]}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** A number field that reports "" as null, so a cleared field clears the value. */
const NumberInput = ({
  value,
  onChange,
}: {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
}) => (
  <Input
    type="number"
    placeholder="0"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
  />
);

interface ConditionsEditorProps {
  conditions: EditableCondition[];
  onChange: (conditions: EditableCondition[]) => void;
  /** Reference's own prompt — the default target for new conditions. */
  ownPromptObjectIdentifier: string;
  /** Group the reference's prompt belongs to. Drives the target-prompt picker. */
  groupObjectIdentifier?: string;
  /**
   * Prompts in the same group as the reference's prompt. Used to render the
   * target-prompt picker and to look up each target's data type so the
   * condition fields match.
   */
  groupPrompts: PromptModel[];
  /** Fallback data type when a target prompt isn't found in `groupPrompts`. */
  ownPromptDataType?: PromptDataType;
}

/**
 * Editor for the conditions attached to an Assembly Reference. Each condition
 * targets a prompt in the same group as the reference's prompt (defaults to
 * the reference's own prompt); the input fields shown adapt to the target
 * prompt's data type.
 */
const ConditionsEditor = ({
  conditions,
  onChange,
  ownPromptObjectIdentifier,
  groupObjectIdentifier,
  groupPrompts,
  ownPromptDataType,
}: ConditionsEditorProps) => {
  const promptByObjectIdentifier = React.useMemo(() => {
    const map = new Map<string, PromptModel>();
    for (const p of groupPrompts) {
      map.set(p.objectIdentifier, p);
    }
    return map;
  }, [groupPrompts]);

  const resolveDataType = (targetPromptObjectIdentifier: string): PromptDataType | undefined =>
    promptByObjectIdentifier.get(targetPromptObjectIdentifier)?.promptDataTypeId ??
    (targetPromptObjectIdentifier === ownPromptObjectIdentifier ? ownPromptDataType : undefined);

  const patch = (index: number, change: Partial<AssemblyReferenceConditionWrite>) => {
    onChange(conditions.map((c, i) => (i === index ? { ...c, ...change } : c)));
  };

  const remove = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...conditions, createEmptyCondition(ownPromptObjectIdentifier)]);
  };

  // `ownPromptObjectIdentifier` is "" before the user has picked a prompt in
  // add mode. In that state there's no sensible default target for a new
  // condition, so we show the same hint the editor used to show.
  if (!ownPromptObjectIdentifier) {
    return <Hint>Select a prompt to add conditions.</Hint>;
  }

  return (
    <div className="flex flex-col gap-2.5">
      {conditions.map((condition, index) => {
        const targetDataType = resolveDataType(condition.targetPromptObjectIdentifier);
        return (
          <EditorCard
            key={condition._key}
            title={`Condition ${index + 1}`}
            removeLabel="Remove condition"
            onRemove={() => remove(index)}
          >
            <TargetPromptPicker
              value={condition.targetPromptObjectIdentifier}
              ownPromptObjectIdentifier={ownPromptObjectIdentifier}
              groupObjectIdentifier={groupObjectIdentifier}
              groupPrompts={groupPrompts}
              onChange={(targetPromptObjectIdentifier) =>
                // When the target changes, the new prompt's data type may differ —
                // reset every data-type-specific field so the saved condition matches
                // what the new field shape will let the user enter.
                patch(index, {
                  targetPromptObjectIdentifier,
                  isYes: null,
                  textAnswer: null,
                  fuzzyMatch: false,
                  decimalAnswer: null,
                  decimalOperator: null,
                  minimumDecimalAnswer: null,
                  maximumDecimalAnswer: null,
                  dateOperator: null,
                  startDate: null,
                  endDate: null,
                  dateAnswer: null,
                  allCategoriesMatch: false,
                  categoryConditions: [],
                })
              }
            />
            {targetDataType !== undefined ? (
              <ConditionFields
                condition={condition}
                promptDataType={targetDataType}
                onPatch={(change) => patch(index, change)}
              />
            ) : (
              <Hint>Pick a target prompt to configure this condition.</Hint>
            )}
          </EditorCard>
        );
      })}
      <AddRowButton onClick={add}>Add condition</AddRowButton>
    </div>
  );
};

interface TargetPromptPickerProps {
  value: string;
  ownPromptObjectIdentifier: string;
  groupObjectIdentifier?: string;
  groupPrompts: PromptModel[];
  onChange: (value: string) => void;
}

/**
 * Per-condition picker for the prompt this condition is evaluated against.
 * Lists prompts in the reference's group, with the reference's own prompt
 * flagged. When the group hasn't been loaded (edit mode without group context)
 * the picker degrades to a read-only label so the user at least sees which
 * prompt the saved condition targets.
 */
const TargetPromptPicker = ({
  value,
  ownPromptObjectIdentifier,
  groupObjectIdentifier,
  groupPrompts,
  onChange,
}: TargetPromptPickerProps) => {
  if (!groupObjectIdentifier || groupPrompts.length === 0) {
    const targetLabel =
      groupPrompts.find((p) => p.objectIdentifier === value)?.label ??
      (value === ownPromptObjectIdentifier ? "This reference's prompt" : "—");
    return (
      <FormSection label="Apply when">
        <div className="text-body2 text-strong">{targetLabel}</div>
      </FormSection>
    );
  }

  return (
    <FormSection label="Apply when">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {groupPrompts.map((prompt) => (
            <SelectItem key={prompt.objectIdentifier} value={prompt.objectIdentifier}>
              {prompt.label}
              {prompt.objectIdentifier === ownPromptObjectIdentifier ? " (this reference)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormSection>
  );
};

/** Yes or No, and neither until one is chosen. Each radio has a label to click. */
const BooleanAnswer = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (isYes: boolean) => void;
}) => {
  const id = React.useId();
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onChange(next === "true")}
      orientation="horizontal"
      className="flex flex-row gap-4 py-1.5"
    >
      {(["true", "false"] as const).map((option) => (
        <div key={option} className="flex flex-row items-center gap-2">
          <RadioGroupItem value={option} id={`${id}-${option}`} />
          <label htmlFor={`${id}-${option}`} className="text-body2">
            {option === "true" ? "Yes" : "No"}
          </label>
        </div>
      ))}
    </RadioGroup>
  );
};

interface ConditionFieldsProps {
  condition: AssemblyReferenceConditionWrite;
  promptDataType: PromptDataType;
  onPatch: (change: Partial<AssemblyReferenceConditionWrite>) => void;
}

/** Renders the data-type-specific inputs for a single condition. */
const ConditionFields = ({ condition, promptDataType, onPatch }: ConditionFieldsProps) => {
  switch (promptDataType) {
    case PromptDataType.Boolean:
      return (
        <FormSection label="Expected answer">
          <BooleanAnswer
            value={
              condition.isYes === null || condition.isYes === undefined
                ? ""
                : String(condition.isYes)
            }
            onChange={(isYes) => onPatch({ isYes })}
          />
        </FormSection>
      );

    case PromptDataType.RangeOfValues:
      return (
        <div className="flex flex-col gap-2.5">
          <FormSection label="Comparison">
            <OperatorSelect
              value={condition.decimalOperator ?? ""}
              onChange={(decimalOperator) => onPatch({ decimalOperator })}
            />
          </FormSection>
          {condition.decimalOperator === RangeComparisonOperator.Range ? (
            <>
              <FormSection label="Minimum">
                <NumberInput
                  value={condition.minimumDecimalAnswer}
                  onChange={(minimumDecimalAnswer) => onPatch({ minimumDecimalAnswer })}
                />
              </FormSection>
              <FormSection label="Maximum">
                <NumberInput
                  value={condition.maximumDecimalAnswer}
                  onChange={(maximumDecimalAnswer) => onPatch({ maximumDecimalAnswer })}
                />
              </FormSection>
            </>
          ) : (
            <FormSection label="Value">
              <NumberInput
                value={condition.decimalAnswer}
                onChange={(decimalAnswer) => onPatch({ decimalAnswer })}
              />
            </FormSection>
          )}
        </div>
      );

    case PromptDataType.Date:
      return (
        <div className="flex flex-col gap-2.5">
          <FormSection label="Comparison">
            <OperatorSelect
              value={condition.dateOperator ?? ""}
              onChange={(dateOperator) => onPatch({ dateOperator })}
            />
          </FormSection>
          {condition.dateOperator === RangeComparisonOperator.Range ? (
            <>
              <FormSection label="From">
                <Input
                  type="date"
                  value={condition.startDate ?? ""}
                  onChange={(e) => onPatch({ startDate: e.target.value || null })}
                />
              </FormSection>
              <FormSection label="To">
                <Input
                  type="date"
                  value={condition.endDate ?? ""}
                  onChange={(e) => onPatch({ endDate: e.target.value || null })}
                />
              </FormSection>
            </>
          ) : (
            <FormSection label="Date">
              <Input
                type="date"
                value={condition.dateAnswer ?? ""}
                onChange={(e) => onPatch({ dateAnswer: e.target.value || null })}
              />
            </FormSection>
          )}
        </div>
      );

    case PromptDataType.ListOfValues:
      return (
        <CategoryConditionsFields
          categoryConditions={condition.categoryConditions}
          allCategoriesMatch={condition.allCategoriesMatch}
          onChange={(categoryConditions, allCategoriesMatch) =>
            onPatch({ categoryConditions, allCategoriesMatch })
          }
        />
      );

    default:
      // Text and Generative prompts
      return (
        <FormSection label="Answer contains">
          <Input
            placeholder="Text to look for in the answer"
            value={condition.textAnswer ?? ""}
            onChange={(e) => onPatch({ textAnswer: e.target.value || null })}
          />
          <OptionCheckbox
            checked={condition.fuzzyMatch}
            onChange={(fuzzyMatch) => onPatch({ fuzzyMatch })}
            label="Match approximately"
          />
        </FormSection>
      );
  }
};

interface CategoryConditionsFieldsProps {
  categoryConditions: AssemblyReferenceCategoryConditionWrite[];
  allCategoriesMatch: boolean;
  onChange: (
    categoryConditions: AssemblyReferenceCategoryConditionWrite[],
    allCategoriesMatch: boolean
  ) => void;
}

const CategoryConditionsFields = ({
  categoryConditions,
  allCategoriesMatch,
  onChange,
}: CategoryConditionsFieldsProps) => {
  const updateCategory = (
    index: number,
    change: Partial<AssemblyReferenceCategoryConditionWrite>
  ) =>
    onChange(
      categoryConditions.map((c, i) => (i === index ? { ...c, ...change } : c)),
      allCategoriesMatch
    );

  return (
    <FormSection label="Categories">
      <div className="flex flex-col gap-1.5">
        {categoryConditions.map((category, index) => (
          // The narrowest row in the pane: a field, a toggle and a remove, side by
          // side inside a card inside a form. "Fuzzy" is a tooltipped checkbox rather
          // than a labelled one because the words do not fit beside the field.
          <div className="flex flex-row items-center gap-0.5" key={index}>
            <Input
              placeholder={`Category ${index + 1}`}
              value={category.text}
              onChange={(e) => updateCategory(index, { text: e.target.value })}
            />
            <Tooltip title="Match this category approximately">
              <Checkbox
                checked={category.fuzzyMatch}
                onCheckedChange={(next) => updateCategory(index, { fuzzyMatch: next === true })}
                aria-label={`Match category ${index + 1} approximately`}
                className="mx-2.5"
              />
            </Tooltip>
            <Tooltip title="Remove category">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove category ${index + 1}`}
                onClick={() =>
                  onChange(
                    categoryConditions.filter((_, i) => i !== index),
                    allCategoriesMatch
                  )
                }
                className="text-muted-foreground hover:text-error"
              >
                <XIcon className="size-4" />
              </Button>
            </Tooltip>
          </div>
        ))}
        <div>
          <Button
            variant="ghost"
            onClick={() =>
              onChange([...categoryConditions, { text: "", fuzzyMatch: false }], allCategoriesMatch)
            }
            className="-ml-2 text-muted-foreground"
          >
            <PlusIcon />
            Add category
          </Button>
        </div>
        <OptionCheckbox
          checked={allCategoriesMatch}
          onChange={(checked) => onChange(categoryConditions, checked)}
          label="All categories must match"
        />
      </div>
    </FormSection>
  );
};

export default ConditionsEditor;
